import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Lightbulb, Plus, Trash2 } from "lucide-react";
import { api, InspirationLink } from "@/lib/api";
import VoiceResearchChat from "@/components/voice/VoiceResearchChat";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const GROUP_LABELS: Record<string, string> = {
  landing_wzorce: "Wzorce landingów",
  antywzorce: "Antywzorce",
  skala_creator: "Skala / creator",
  nisza_pl: "Nisza PL",
  nisza_global: "Nisza global",
};

export default function Inspiracje() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [tag, setTag] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addUrl, setAddUrl] = useState("");
  const [addGroup, setAddGroup] = useState("landing_wzorce");
  const [addTags, setAddTags] = useState("");

  const catalogQ = useQuery({
    queryKey: ["inspiration-catalog", q, group, tag],
    queryFn: () =>
      api.inspirationCatalog({
        q: q.trim() || undefined,
        group: group || undefined,
        tag: tag || undefined,
      }),
  });

  const items = catalogQ.data?.items || [];
  const groups = catalogQ.data?.groups || [];
  const allTags = catalogQ.data?.tags || [];

  const selected: InspirationLink | null = useMemo(() => {
    if (!items.length) return null;
    const hit = items.find((i) => i.id === selectedId);
    return hit || items[0] || null;
  }, [items, selectedId]);

  useEffect(() => {
    if (selected && selected.id !== selectedId) {
      setSelectedId(selected.id);
    }
  }, [selected, selectedId]);

  useEffect(() => {
    setNotesDraft(selected?.notes || "");
    setIframeBlocked(false);
  }, [selected?.id, selected?.notes]);

  const notesM = useMutation({
    mutationFn: (notes: string) => api.inspirationPutNotes(selected!.id, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inspiration-catalog"] });
    },
  });

  useEffect(() => {
    if (!selected) return;
    const current = selected.notes || "";
    if (notesDraft === current) return;
    const t = window.setTimeout(() => {
      notesM.mutate(notesDraft);
    }, 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, selected?.id]);

  const addM = useMutation({
    mutationFn: () =>
      api.inspirationCreateLink({
        title: addTitle.trim(),
        url: addUrl.trim(),
        group: addGroup,
        tags: addTags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      }),
    onSuccess: (item) => {
      setShowAdd(false);
      setAddTitle("");
      setAddUrl("");
      setAddTags("");
      setSelectedId(item.id);
      void qc.invalidateQueries({ queryKey: ["inspiration-catalog"] });
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.inspirationDeleteLink(id),
    onSuccess: () => {
      setSelectedId(null);
      void qc.invalidateQueries({ queryKey: ["inspiration-catalog"] });
    },
  });

  const pageContext = useMemo(() => {
    if (!selected) return null;
    return {
      link_id: selected.id,
      title: selected.title,
      url: selected.url,
      group: selected.group,
      tags: selected.tags,
      notes: notesDraft,
      blurb: selected.blurb || "",
    };
  }, [selected, notesDraft]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Lightbulb className="h-6 w-6 text-amber-600" />
            Inspiracje
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Linki z playbooka, Barta i benchmarków. Podgląd w iframe (best-effort),
            notatki i chat o treści strony.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowAdd((v) => !v)}>
          <Plus className="mr-1 h-4 w-4" />
          Dodaj link
        </Button>
      </div>

      {showAdd && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Nowy link</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            <Input
              placeholder="Tytuł"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
            />
            <Input
              placeholder="https://…"
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
            />
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={addGroup}
              onChange={(e) => setAddGroup(e.target.value)}
            >
              {Object.entries(GROUP_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <Input
              placeholder="Tagi (przecinek)"
              value={addTags}
              onChange={(e) => setAddTags(e.target.value)}
            />
            <div className="flex gap-2 sm:col-span-2">
              <Button
                type="button"
                size="sm"
                disabled={!addTitle.trim() || !addUrl.trim() || addM.isPending}
                onClick={() => addM.mutate()}
              >
                Zapisz
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setShowAdd(false)}
              >
                Anuluj
              </Button>
              {addM.isError && (
                <span className="text-sm text-rose-600">
                  {(addM.error as Error)?.message || "Błąd zapisu"}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <Card className="flex w-full flex-col lg:sticky lg:top-0 lg:h-[calc(100dvh-5.5rem)] lg:max-h-[calc(100dvh-5.5rem)] lg:w-[280px] lg:shrink-0">
          <CardHeader className="space-y-2 pb-2">
            <Input
              placeholder="Szukaj…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setGroup("")}
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] font-medium",
                  !group
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                )}
              >
                Wszystkie
              </button>
              {(groups.length
                ? groups
                : Object.entries(GROUP_LABELS).map(([id, label]) => ({
                    id,
                    label,
                  }))
              ).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGroup(group === g.id ? "" : g.id)}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium",
                    group === g.id
                      ? "bg-amber-700 text-white"
                      : "bg-amber-50 text-amber-900 hover:bg-amber-100",
                  )}
                >
                  {g.label}
                </button>
              ))}
            </div>
            {allTags.length > 0 && (
              <div className="flex max-h-16 flex-wrap gap-1 overflow-y-auto">
                {allTags.slice(0, 40).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTag(tag === t ? "" : t)}
                    className={cn(
                      "rounded px-1.5 py-0.5 font-mono text-[10px]",
                      tag === t
                        ? "bg-sky-700 text-white"
                        : "bg-sky-50 text-sky-800 hover:bg-sky-100",
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent className="flex-1 space-y-1.5 overflow-y-auto p-3 pt-0">
            {catalogQ.isLoading && (
              <p className="py-6 text-sm text-muted-foreground">Wczytywanie…</p>
            )}
            {catalogQ.isError && (
              <p className="py-6 text-sm text-rose-600">Nie udało się wczytać katalogu.</p>
            )}
            {!catalogQ.isLoading && items.length === 0 && (
              <p className="py-6 text-sm text-muted-foreground">Brak wyników.</p>
            )}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                className={cn(
                  "w-full rounded-lg border px-3 py-2 text-left transition-colors",
                  selected?.id === item.id
                    ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400/40"
                    : "border-slate-200 bg-white hover:bg-slate-50",
                )}
              >
                <div className="truncate text-sm font-medium">{item.title}</div>
                <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {GROUP_LABELS[item.group] || item.group}
                  {item.has_notes ? " · notatki" : ""}
                  {item.origin === "user" ? " · własne" : ""}
                </div>
                {item.tags?.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.tags.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[9px] text-slate-600"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="flex min-w-0 flex-1 flex-col overflow-hidden lg:sticky lg:top-0 lg:h-[calc(100dvh-5.5rem)] lg:max-h-[calc(100dvh-5.5rem)]">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 py-3">
            <div className="min-w-0">
              <CardTitle className="truncate text-base">
                {selected?.title || "Wybierz link"}
              </CardTitle>
              {selected?.blurb ? (
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                  {selected.blurb}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {selected?.origin === "user" && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-rose-600"
                  disabled={deleteM.isPending}
                  onClick={() => {
                    if (selected && window.confirm("Usunąć ten link?")) {
                      deleteM.mutate(selected.id);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              {selected?.url ? (
                <Button type="button" size="sm" variant="outline" asChild>
                  <a href={selected.url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1 h-3.5 w-3.5" />
                    Otwórz live
                  </a>
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="relative flex min-h-0 flex-1 flex-col p-0">
            {selected?.url ? (
              <>
                <iframe
                  key={selected.id}
                  title={selected.title}
                  src={selected.url}
                  className="min-h-[280px] w-full flex-1 border-t bg-white lg:min-h-0"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                  referrerPolicy="no-referrer"
                  onError={() => setIframeBlocked(true)}
                  onLoad={() => {
                    setIframeBlocked(false);
                  }}
                />
                <p className="border-t bg-amber-50 px-3 py-1.5 text-[11px] text-amber-900">
                  {iframeBlocked
                    ? "Strona prawdopodobnie blokuje osadzenie. Użyj „Otwórz live”; chat i tak dostanie ekstrakt z serwera."
                    : "Jeśli iframe jest pusty, strona blokuje framing (X-Frame-Options / CSP). Chat działa na ekstrakcie serwerowym."}
                </p>
              </>
            ) : (
              <div className="flex h-48 flex-1 items-center justify-center text-sm text-muted-foreground">
                Wybierz link z listy.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex w-full flex-col gap-3 lg:sticky lg:top-0 lg:h-[calc(100dvh-5.5rem)] lg:max-h-[calc(100dvh-5.5rem)] lg:min-w-[560px] lg:flex-[2] lg:shrink-0">
          <Card className="shrink-0">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Notatki</CardTitle>
            </CardHeader>
            <CardContent className="pb-3 pt-0">
              <textarea
                className="min-h-[100px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Co zauważasz: struktura, CTA, ton, rzeczy do przeniesienia…"
                value={notesDraft}
                disabled={!selected}
                onChange={(e) => setNotesDraft(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {notesM.isPending
                  ? "Zapisywanie…"
                  : notesM.isSuccess
                    ? "Zapisano"
                    : "Autosave po pauzie w pisaniu"}
              </p>
            </CardContent>
          </Card>

          <VoiceResearchChat
            surface="inspiration_chat"
            pageContext={pageContext}
            className="min-h-0 flex-1 rounded-xl border border-slate-200"
          />
        </div>
      </div>
    </div>
  );
}
